import React, { useEffect, useMemo } from "react";
import {
  Grid,
  Select,
  NumberInput,
  Stack,
} from "@mantine/core";
import { DATA_AR_COMMISSION_ACCOUNT_TYPE } from "../data/options";
import {
  AR_FORM_NUMBER_INPUT_PROPS,
  getArFormValues,
  setCommissionAccountType,
  updateCommission,
} from "../helpers/arFormUtils";
import { useInvestmentTransactionSettings } from "../contexts/investmentTransactionSettingsContext";
import { resolveMinimumNonWrapUpfrontFee } from "../helpers/investmentTransactionSettingsUtils";
import { resolveNonWrapEffectiveMinimum } from "../helpers/investmentTransactionCommissionUtils";
import OrderArFormBlock, { OrderArFormStack } from "./orderArFormBlock";

const OrderArFormPartC = ({ form }) => {
  const arForm = getArFormValues(form);
  const commission = arForm.commission ?? {};
  const isWrap = commission.accountType === "Wrap";
  const isNonWrap = commission.accountType === "NonWrap";
  const { nonWrapUpfrontFeeRules } = useInvestmentTransactionSettings();
  const signDate = arForm.advisorDeclaration?.signDate;
  const submissionVia = arForm.submissionVia || "Online";
  const minUpfrontFee = useMemo(
    () =>
      resolveNonWrapEffectiveMinimum({
        submissionVia,
        settingsMin: resolveMinimumNonWrapUpfrontFee(
          nonWrapUpfrontFeeRules,
          signDate
        ),
      }),
    [nonWrapUpfrontFeeRules, signDate, submissionVia]
  );

  useEffect(() => {
    if (!isNonWrap) {
      form.clearFieldError?.("arForm.commission.upfrontFee");
      return;
    }
    form.validateField?.("arForm.commission.upfrontFee");
  }, [isNonWrap, commission.upfrontFee, minUpfrontFee, signDate, submissionVia, form]);

  return (
    <OrderArFormStack>
      <OrderArFormBlock
        title="Commission & fees"
        subtitle="Account type and fee disclosure for the AR Form"
      >
        <Stack spacing="md">
          <Select
            label="Account type 賬戶類別"
            data={DATA_AR_COMMISSION_ACCOUNT_TYPE}
            value={commission.accountType ?? null}
            onChange={(v) => setCommissionAccountType(form, v)}
            searchable
            clearable
          />

          {isWrap && (
            <Grid gutter="md">
              <Grid.Col span={12} md={6}>
                <NumberInput
                  label="Upfront wrap fee / switch fee % 首次包管費 / 轉換費"
                  value={commission.upfrontWrapFee ?? ""}
                  onChange={(v) => updateCommission(form, { upfrontWrapFee: v })}
                  precision={2}
                  min={0}
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Grid.Col>
              <Grid.Col span={12} md={6}>
                <NumberInput
                  label="Ongoing wrap fee % 年度包管費"
                  value={commission.ongoingWrapFee ?? ""}
                  onChange={(v) => updateCommission(form, { ongoingWrapFee: v })}
                  precision={2}
                  min={0}
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Grid.Col>
              <Grid.Col span={12} md={6}>
                <NumberInput
                  label="Platform fee % 平台服務費 (fixed on form 表格固定)"
                  value={0.3}
                  readOnly
                  precision={2}
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Grid.Col>
            </Grid>
          )}

          {isNonWrap && (
            <Grid gutter="md">
              <Grid.Col span={12} md={6}>
                <NumberInput
                  label="Upfront fee / switch fee % 銷售費 / 轉換費"
                  description={`Minimum ${minUpfrontFee}% (Online: 0.3%, Paper: 0.5%)`}
                  required
                  error={form.errors?.["arForm.commission.upfrontFee"]}
                  value={commission.upfrontFee ?? ""}
                  onChange={(v) =>
                    updateCommission(form, { upfrontFee: v, switchFee: undefined })
                  }
                  precision={2}
                  min={0}
                  {...AR_FORM_NUMBER_INPUT_PROPS}
                />
              </Grid.Col>
            </Grid>
          )}
        </Stack>
      </OrderArFormBlock>
    </OrderArFormStack>
  );
};

export default OrderArFormPartC;
